import { type ReactNode, useEffect, useRef, useState } from 'react';

import { ChevronDown, ChevronUp } from 'lucide-react';

function getScrollParent(el: HTMLElement | null): HTMLElement | Window {
    let parent = el?.parentElement;
    while (parent) {
        const { overflowY } = getComputedStyle(parent);
        if (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') {
            if (parent.scrollHeight > parent.clientHeight) return parent;
        }
        parent = parent.parentElement;
    }
    return window;
}

type Props = {
    defaultShowOnlySelected?: boolean;
    children: (props: { showOnlySelected: boolean }) => ReactNode;
};

export function OptionsVisibilityToggle({ defaultShowOnlySelected, children }: Props) {
    const [showOnlySelected, setShowOnlySelected] = useState(defaultShowOnlySelected ?? true);
    const savedScrollRef = useRef<{ target: HTMLElement | Window; top: number } | null>(null);
    const previousRef = useRef(showOnlySelected);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!previousRef.current && showOnlySelected && savedScrollRef.current) {
            savedScrollRef.current.target.scrollTo({
                top: savedScrollRef.current.top,
                behavior: 'smooth',
            });
        }
        previousRef.current = showOnlySelected;
    }, [showOnlySelected]);

    const handleClick = () => {
        if (showOnlySelected) {
            const target = getScrollParent(containerRef.current);
            const top = target instanceof Window ? target.scrollY : target.scrollTop;
            savedScrollRef.current = { target, top };
        }
        setShowOnlySelected(!showOnlySelected);
    };

    return (
        <div ref={containerRef} className="flex flex-col gap-5 scroll-mt-4">
            {children({ showOnlySelected })}
            <button
                type="button"
                onClick={handleClick}
                className="text-muted-foreground hover:text-foreground flex items-center gap-2 self-start font-geist font-medium text-sm"
            >
                {showOnlySelected ? 'Show all options' : 'Show only selected options'}
                {showOnlySelected ? (
                    <ChevronDown className="size-4" aria-hidden />
                ) : (
                    <ChevronUp className="size-4" aria-hidden />
                )}
            </button>
        </div>
    );
}
